// Columna izquierda del explorer (`.col-filters` del diseño): búsqueda · Nivel · Estado · Bloqueo ·
// Producto · Tienda · Días de mora · Saldo · Cuota · Fecha de originación. La búsqueda va con
// debounce; el resto sube al padre en el click.
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/lib/cn';
import { CLIENT_LEVEL_LABEL, CREDIT_STATUS_PRESENTATION } from '../../creditFile/logic';
import { ARREARS_BUCKET_LABEL, LOCK_FILTER_LABEL } from '../format';
import { activeFilterCount, dateShortcutRange } from '../logic';
import {
    ANY_FILTER,
    emptyPortfolioFilters,
    type ArrearsBucket,
    type PortfolioFilters,
    type PortfolioLockState,
} from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

/** Los cuatro chips de Nivel del diseño. «Empleado» no existe en el enum del silo. */
const LEVEL_CHIPS: readonly ClientLevelEnum[] = [
    ClientLevelEnum.GOLD,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.BRONZE,
    ClientLevelEnum.NOT_ELIGIBLE,
];

const ARREARS_BUCKETS: readonly ArrearsBucket[] = ['none', '1-7', '8-30', '31-60', '61-90'];

// `unknown` no se ofrece: cuando la flota no se pudo leer TODAS las filas lo son, y filtrar por él
// no partiría la lista en nada.
const LOCK_STATES: readonly PortfolioLockState[] = ['locked', 'unlocked', 'no-device'];

const DATE_SHORTCUTS: { label: string; days: number }[] = [
    { label: 'Hoy', days: 0 },
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: 'Trim', days: 90 },
    { label: 'Todo', days: -1 },
];

/** Un campo numérico en PESOS que sube a centavos; vacío es `null` («sin tope»), no cero. */
function MoneyInput({
    value,
    placeholder,
    ariaLabel,
    onChange,
}: {
    value: number | null;
    placeholder: string;
    ariaLabel: string;
    onChange: (cents: number | null) => void;
}) {
    return (
        <input
            type="number"
            min={0}
            inputMode="numeric"
            aria-label={ariaLabel}
            placeholder={placeholder}
            value={value === null ? '' : String(value / 100)}
            onChange={(event) => {
                const raw = event.target.value.trim();
                onChange(raw === '' ? null : Math.round(Number(raw) * 100));
            }}
            className={cn(FIELD, 'tabular-nums')}
        />
    );
}

export interface PortfolioFiltersPanelProps {
    filters: PortfolioFilters;
    onChange: (next: PortfolioFilters) => void;
    /** Opciones que salen de las filas. Sin fuente llegan vacías y el combo no se pinta. */
    brands: readonly string[];
    stores: readonly string[];
    /** Que los filtros miran lo cargado y no la cartera. `null` cuando ya no quedan páginas. */
    loadedNote?: string | null;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function PortfolioFiltersPanel({
    filters,
    onChange,
    brands,
    stores,
    loadedNote = null,
    collapsed,
    onToggleCollapsed,
}: PortfolioFiltersPanelProps) {
    const [searchText, setSearchText] = useState(filters.search);
    const debouncedSearch = useDebounce(searchText, 250);
    const emittedSearch = useRef(filters.search);

    // Sube la búsqueda ya reposada; el guard evita re-emitir cuando cambia otro filtro.
    useEffect(() => {
        if (debouncedSearch === emittedSearch.current) return;
        emittedSearch.current = debouncedSearch;
        onChange({ ...filters, search: debouncedSearch });
    }, [debouncedSearch, filters, onChange]);

    // Baja a la caja local un reset hecho desde afuera.
    useEffect(() => {
        if (filters.search === emittedSearch.current) return;
        emittedSearch.current = filters.search;
        setSearchText(filters.search);
    }, [filters.search]);

    const patch = (partial: Partial<PortfolioFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const toggleLevel = (level: ClientLevelEnum) => {
        const next = filters.levels.includes(level)
            ? filters.levels.filter((item) => item !== level)
            : [...filters.levels, level];
        patch({ levels: next });
    };

    const clearAll = () => {
        emittedSearch.current = '';
        setSearchText('');
        onChange(emptyPortfolioFilters());
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeFilterCount(filters)}
            onReset={clearAll}
            titleAdornment={
                loadedNote === null ? null : (
                    <span className="rounded bg-warning/15 px-1 py-px text-[9px] font-bold uppercase tracking-[0.06em] text-foreground">
                        Sobre lo cargado
                    </span>
                )
            }
        >
            {loadedNote === null ? null : (
                <div
                    role="status"
                    className="mt-2 rounded border border-border bg-muted/40 px-2 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </div>
            )}

            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="text"
                        value={searchText}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                        }}
                        placeholder="Crédito, cliente o IMEI"
                        aria-label="Buscar por crédito, cliente o IMEI"
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {searchText === '' ? null : (
                        <button
                            type="button"
                            onClick={() => {
                                emittedSearch.current = '';
                                setSearchText('');
                                patch({ search: '' });
                            }}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Nivel</div>
                <div className="flex flex-wrap gap-1">
                    {LEVEL_CHIPS.map((level) => {
                        const on = filters.levels.includes(level);
                        return (
                            <button
                                key={level}
                                type="button"
                                aria-pressed={on}
                                onClick={() => {
                                    toggleLevel(level);
                                }}
                                className={cn(
                                    'rounded border px-1.5 py-[2px] text-[10.5px] transition-colors',
                                    on
                                        ? 'border-primary bg-primary/10 font-semibold text-primary'
                                        : 'border-border text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {CLIENT_LEVEL_LABEL[level]}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Estado</div>
                <select
                    value={filters.status}
                    aria-label="Estado del crédito"
                    onChange={(event) => {
                        patch({ status: event.target.value as PortfolioFilters['status'] });
                    }}
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {Object.values(LoanCreditStatusEnum).map((status) => (
                        <option key={status} value={status}>
                            {CREDIT_STATUS_PRESENTATION[status].label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Bloqueo</div>
                <select
                    value={filters.lock}
                    aria-label="Estado de bloqueo"
                    onChange={(event) => {
                        patch({ lock: event.target.value as PortfolioFilters['lock'] });
                    }}
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {LOCK_STATES.map((state) => (
                        <option key={state} value={state}>
                            {LOCK_FILTER_LABEL[state]}
                        </option>
                    ))}
                </select>
            </div>

            {/* Sin filas no hay marcas ni tiendas que ofrecer: un combo con solo «Todos» no filtra nada. */}
            {brands.length === 0 ? null : (
                <div className={GROUP}>
                    {/* Rótulo «Producto» como el mock, aunque las opciones sean marcas. */}
                    <div className={LABEL}>Producto</div>
                    <select
                        value={filters.brand}
                        aria-label="Producto"
                        onChange={(event) => {
                            patch({ brand: event.target.value });
                        }}
                        className={FIELD}
                    >
                        <option value={ANY_FILTER}>Todos</option>
                        {brands.map((brand) => (
                            <option key={brand} value={brand}>
                                {brand}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {stores.length === 0 ? null : (
                <div className={GROUP}>
                    <div className={LABEL}>Tienda</div>
                    <select
                        value={filters.store}
                        aria-label="Tienda"
                        onChange={(event) => {
                            patch({ store: event.target.value });
                        }}
                        className={FIELD}
                    >
                        <option value={ANY_FILTER}>Todas las tiendas</option>
                        {stores.map((store) => (
                            <option key={store} value={store}>
                                {store}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className={GROUP}>
                <div className={LABEL}>Días de mora</div>
                <select
                    value={filters.arrears}
                    aria-label="Días de mora"
                    onChange={(event) => {
                        patch({ arrears: event.target.value as PortfolioFilters['arrears'] });
                    }}
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {ARREARS_BUCKETS.map((bucket) => (
                        <option key={bucket} value={bucket}>
                            {ARREARS_BUCKET_LABEL[bucket]}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                {/* Filtra por capital: la deuda total de la tabla incluye interés e IVA. */}
                <div className={LABEL}>Saldo insoluto (capital)</div>
                <div className="grid grid-cols-2 gap-1">
                    <MoneyInput
                        value={filters.balanceMinCents}
                        placeholder="Mín"
                        ariaLabel="Saldo insoluto mínimo"
                        onChange={(cents) => {
                            patch({ balanceMinCents: cents });
                        }}
                    />
                    <MoneyInput
                        value={filters.balanceMaxCents}
                        placeholder="Máx"
                        ariaLabel="Saldo insoluto máximo"
                        onChange={(cents) => {
                            patch({ balanceMaxCents: cents });
                        }}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Cuota semanal</div>
                <div className="grid grid-cols-2 gap-1">
                    <MoneyInput
                        value={filters.paymentMinCents}
                        placeholder="Mín"
                        ariaLabel="Cuota semanal mínima"
                        onChange={(cents) => {
                            patch({ paymentMinCents: cents });
                        }}
                    />
                    <MoneyInput
                        value={filters.paymentMaxCents}
                        placeholder="Máx"
                        ariaLabel="Cuota semanal máxima"
                        onChange={(cents) => {
                            patch({ paymentMaxCents: cents });
                        }}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Fecha de originación</div>
                <input
                    type="date"
                    value={filters.fromDay}
                    aria-label="Originado desde"
                    onChange={(event) => {
                        patch({ fromDay: event.target.value });
                    }}
                    className={cn(FIELD, 'mb-1')}
                />
                <input
                    type="date"
                    value={filters.toDay}
                    aria-label="Originado hasta"
                    onChange={(event) => {
                        patch({ toDay: event.target.value });
                    }}
                    className={cn(FIELD, 'mb-1')}
                />
                <div className="flex flex-wrap gap-1">
                    {DATE_SHORTCUTS.map((shortcut) => (
                        <button
                            key={shortcut.label}
                            type="button"
                            onClick={() => {
                                patch(dateShortcutRange(shortcut.days));
                            }}
                            className="rounded border border-border px-1.5 py-[2px] text-[10px] text-muted-foreground transition-colors hover:text-foreground"
                        >
                            {shortcut.label}
                        </button>
                    ))}
                </div>
            </div>
        </FiltersColumn>
    );
}
